import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import type { TransactionsListFilter } from '../lib/filters';
import { transactionApi, type OptionsFilter } from './transaction-api';

/** Query-key factory; the cancel mutation invalidates through `lists()`. */
export const transactionKeys = {
  all: ['report-transactions'] as const,
  lists: () => [...transactionKeys.all, 'list'] as const,
  list: (filter: TransactionsListFilter) => [...transactionKeys.lists(), filter] as const,
  clientOptions: (filter: Omit<OptionsFilter, 'skip'>) =>
    [...transactionKeys.all, 'client-options', filter] as const,
  bonusOptions: (filter: Omit<OptionsFilter, 'skip'>) =>
    [...transactionKeys.all, 'bonus-options', filter] as const,
  foulOptions: (filter: Omit<OptionsFilter, 'skip'>) =>
    [...transactionKeys.all, 'foul-options', filter] as const,
};

/** Next `skip` for an options page, or undefined once every row is loaded. */
function nextOptionsSkip(
  lastPage: { data: unknown[]; count: number },
  pages: { data: unknown[] }[],
): number | undefined {
  const loaded = pages.reduce((total, page) => total + page.data.length, 0);
  if (lastPage.data.length === 0 || loaded >= lastPage.count) return undefined;
  return loaded;
}

export const transactionQueries = {
  list: (filter: TransactionsListFilter) =>
    queryOptions({
      queryKey: transactionKeys.list(filter),
      queryFn: () => transactionApi.list(filter),
      // Keep the previous page on screen while the next one loads.
      placeholderData: (prev) => prev,
    }),

  clientOptions: (filter: Omit<OptionsFilter, 'skip'>, enabled: boolean) =>
    infiniteQueryOptions({
      queryKey: transactionKeys.clientOptions(filter),
      queryFn: ({ pageParam }) =>
        transactionApi.clientOptions({ ...filter, skip: pageParam }),
      initialPageParam: 0,
      getNextPageParam: nextOptionsSkip,
      enabled,
    }),

  bonusOptions: (filter: Omit<OptionsFilter, 'skip'>, enabled: boolean) =>
    infiniteQueryOptions({
      queryKey: transactionKeys.bonusOptions(filter),
      queryFn: ({ pageParam }) =>
        transactionApi.bonusOptions({ ...filter, skip: pageParam }),
      initialPageParam: 0,
      getNextPageParam: nextOptionsSkip,
      enabled,
    }),

  foulOptions: (filter: Omit<OptionsFilter, 'skip'>, enabled: boolean) =>
    infiniteQueryOptions({
      queryKey: transactionKeys.foulOptions(filter),
      queryFn: ({ pageParam }) =>
        transactionApi.foulOptions({ ...filter, skip: pageParam }),
      initialPageParam: 0,
      getNextPageParam: nextOptionsSkip,
      enabled,
    }),
};
